/**
 * Lista de asignaciones de reponedores a almacenes
 */
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Card,
    Typography,
    Button,
    Stack,
    useTheme,
    useMediaQuery
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BadgeIcon from "@mui/icons-material/Badge";

import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_INITIAL_STATE,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { StateContext } from "../../../components/context/StateProvider";
import ReponedoresService from "../api/reponedores.api";
import UsuariosService from "../../../services/usuarios.service";
import Toasts from "../../../components/notificacion/Toasts";
import { ModalEliminar, ReponedoresMobileList } from "../components";
import { generateReponedorColumns } from "../helpers/columns";

const Reponedores = () => {
    const { state, updateState, recargarAsignacionesReponedores } = useContext(StateContext);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const [reponedores, setReponedores] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal de eliminación
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [reponedorToDelete, setReponedorToDelete] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        try {
            const [reponedoresData, usuariosData] = await Promise.all([
                ReponedoresService.list(),
                UsuariosService.listarUsuariosMap()
            ]);

            setReponedores(reponedoresData);
            setUsuarios(usuariosData);
            setLoading(false);
        } catch (error) {
            const toasts = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toasts] });
            setLoading(false);
        }
    };

    const handleNuevo = () => {
        navigate('/reponedores/nueva-asignacion');
    };

    const handleEdit = (id) => {
        navigate(`/reponedores/editar/${id}`);
    };

    const handleDelete = (id) => {
        const reponedor = reponedores.find(r => r.id === id);
        if (!reponedor) return;

        setReponedorToDelete(reponedor);
        setShowModalEliminar(true);
    };

    const cerrarModalEliminar = () => {
        setShowModalEliminar(false);
        setReponedorToDelete(null);
    };

    const confirmarEliminacion = async () => {
        if (!reponedorToDelete) return;

        try {
            setLoadingDelete(true);
            await ReponedoresService.delete(reponedorToDelete.id);
            const toasts = Toasts.generateToast('Asignación eliminada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toasts] });
            cerrarModalEliminar();
            await cargarDatos();

            // Recargar asignaciones en el contexto global
            if (recargarAsignacionesReponedores) {
                await recargarAsignacionesReponedores();
            }
        } catch (error) {
            const toasts = Toasts.generateToast(error.message, 'error');
            updateState({ toasts: [...state.toasts, toasts] });
        } finally {
            setLoadingDelete(false);
        }
    };


    return (
        <Box id="reponedores" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", sm: "center" }}
                        spacing={2}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <BadgeIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Reponedores
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Gestiona las asignaciones de reponedores
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleNuevo}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Añadir asignación
                        </Button>
                    </Stack>
                </Box>

                {/* Contenido */}
                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {isMobile ? (
                        <ReponedoresMobileList
                            reponedores={reponedores}
                            almacenes={state.almacenes}
                            usuarios={usuarios}
                            loading={loading}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ) : (
                        <DataGrid
                            rows={reponedores}
                            columns={generateReponedorColumns(usuarios, state.almacenes, { onEditar: handleEdit, onEliminar: handleDelete })}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            initialState={DATA_GRID_INITIAL_STATE}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            disableRowSelectionOnClick
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    )}
                </Box>
            </Card>

            {/* Modal de confirmación de eliminación */}
            <ModalEliminar
                show={showModalEliminar}
                onHide={cerrarModalEliminar}
                reponedor={reponedorToDelete}
                almacenes={state.almacenes}
                usuarios={usuarios}
                onConfirm={confirmarEliminacion}
                loading={loadingDelete}
            />
        </Box>
    );
};

export default Reponedores;
